import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Building2, 
  TrendingUp, 
  Wallet, 
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
  Plus,
  ChevronRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { getPortfolioSummary, formatCurrency, formatPercent } from "@/lib/api";

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, isLoading }) => (
  <Card className="metric-card overflow-hidden" data-testid={`metric-${title.toLowerCase().replace(/\s/g, '-')}`}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">{title}</p>
          {isLoading ? (
            <Skeleton className="h-8 w-32" />
          ) : (
            <p className="text-2xl lg:text-3xl font-heading font-bold text-slate-900">{value}</p>
          )}
          {subtitle && !isLoading && (
            <p className="text-sm text-slate-500 flex items-center gap-1">
              {trend === "up" && <ArrowUpRight className="w-4 h-4 text-emerald-500" />}
              {trend === "down" && <ArrowDownRight className="w-4 h-4 text-red-500" />}
              {subtitle}
            </p>
          )}
        </div>
        <div className="w-12 h-12 rounded-xl bg-[#547d91]/10 flex items-center justify-center">
          <Icon className="w-6 h-6 text-[#547d91]" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const PropertyRow = ({ property }) => (
  <Link 
    to={`/properties/${property.id}`}
    className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-lg transition-colors group"
    data-testid={`property-row-${property.id}`}
  >
    <div className="flex items-center gap-4">
      <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center">
        <Building2 className="w-6 h-6 text-slate-600" />
      </div>
      <div>
        <p className="font-medium text-slate-900">{property.address}</p>
        <p className="text-sm text-slate-500">{property.suburb}, {property.state}</p>
      </div>
    </div>
    <div className="flex items-center gap-8">
      <div className="text-right hidden sm:block">
        <p className="font-semibold text-slate-900">{formatCurrency(property.current_value)}</p>
        <p className="text-sm text-slate-500">Value</p>
      </div>
      <div className="text-right hidden md:block">
        <p className="font-semibold text-emerald-600">{formatCurrency(property.equity)}</p>
        <p className="text-sm text-slate-500">Equity</p>
      </div>
      <div className="text-right hidden lg:block">
        <p className="font-semibold text-slate-900">{formatPercent(property.lvr)}</p>
        <p className="text-sm text-slate-500">LVR</p>
      </div>
      <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
    </div>
  </Link>
);

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const response = await getPortfolioSummary();
        setSummary(response.data);
      } catch (error) {
        console.error("Error fetching portfolio summary:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSummary();
  }, []);

  const hasProperties = summary && summary.total_properties > 0;

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
            Portfolio Overview
          </h2>
          <p className="text-slate-500 mt-1">
            Track your property investments and wealth growth
          </p>
        </div>
        <Link to="/properties/new">
          <Button 
            className="bg-[#547d91] hover:bg-[#456a7a] text-white rounded-full px-6"
            data-testid="add-property-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </Link>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
        <MetricCard
          title="Total Value"
          value={formatCurrency(summary?.total_value || 0)}
          subtitle={`${summary?.total_properties || 0} properties`}
          icon={Building2}
          isLoading={isLoading}
        />
        <MetricCard
          title="Total Equity"
          value={formatCurrency(summary?.total_equity || 0)}
          subtitle="Available to leverage"
          icon={Wallet}
          trend="up"
          isLoading={isLoading}
        />
        <MetricCard
          title="Total Debt"
          value={formatCurrency(summary?.total_debt || 0)}
          subtitle={`${formatPercent(summary?.average_lvr || 0)} avg LVR`}
          icon={PiggyBank}
          isLoading={isLoading}
        />
        <MetricCard
          title="Annual Rental"
          value={formatCurrency(summary?.total_annual_rental || 0)}
          subtitle={`Net: ${formatCurrency(summary?.net_annual_cashflow || 0)}`}
          icon={TrendingUp}
          trend={summary?.net_annual_cashflow > 0 ? "up" : "down"}
          isLoading={isLoading}
        />
      </div>

      {/* Properties List */}
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4">
          <CardTitle className="font-heading text-xl">Your Properties</CardTitle>
          {hasProperties && (
            <Link to="/properties">
              <Button variant="ghost" className="text-[#547d91]" data-testid="view-all-properties">
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          )}
        </CardHeader>
        <CardContent className="p-2">
          {isLoading ? (
            <div className="space-y-4 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="w-12 h-12 rounded-lg" />
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                </div>
              ))}
            </div>
          ) : hasProperties ? (
            <div className="divide-y divide-slate-100">
              {summary.properties_breakdown.slice(0, 5).map((property) => (
                <PropertyRow key={property.id} property={property} />
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="font-heading text-lg font-semibold text-slate-700 mb-2">
                No properties yet
              </h3>
              <p className="text-slate-500 mb-4">
                Start building your portfolio by adding your first property
              </p>
              <Link to="/properties/new">
                <Button 
                  className="bg-[#547d91] hover:bg-[#456a7a] text-white rounded-full"
                  data-testid="add-first-property-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Property
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      {hasProperties && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/forecasting" className="group">
            <Card className="card-hover h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Growth Forecast</h3>
                  <p className="text-sm text-slate-500">Project future values</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/cashflow" className="group">
            <Card className="card-hover h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center group-hover:bg-amber-100 transition-colors">
                  <Wallet className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Cashflow Analysis</h3>
                  <p className="text-sm text-slate-500">Income, expenses & yield</p>
                </div>
              </CardContent>
            </Card>
          </Link>
          <Link to="/recommendations" className="group">
            <Card className="card-hover h-full">
              <CardContent className="p-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center group-hover:bg-emerald-100 transition-colors">
                  <PiggyBank className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">Buy/Sell Timing</h3>
                  <p className="text-sm text-slate-500">Smart recommendations</p>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>
      )}
    </div>
  );
}
